import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Phone, Mail, LogIn, Menu, X, ChevronDown, ShoppingCart, Calculator, Users, ExternalLink, BookOpen, Briefcase, MapPin, Shield, CreditCard, Image, FileText, Building2, HelpCircle, Snowflake, Trees, Car, Paintbrush, MessageSquare, Clock, Wrench, Award, Camera, FileCheck, Percent, AlertOctagon, LayoutGrid } from 'lucide-react';
import { companyInfo } from '../../data/mock';
import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { VinylFenceIcon, WoodFenceIcon, ChainLinkIcon, OrnamentalFenceIcon } from '../icons/FenceIcons';

import landingLogoColor from '../../assets/logos/landing/logo-color.png';
import fenceLogoColor from '../../assets/logos/fence/logo-color.png';
import psLogoColor from '../../assets/logos/ps/logo-color.png';

const STORE_URL = 'https://mccsys.ca/store/central-alberta-fencing-inc-la24fw';
const FENCE_PORTAL_URL = 'https://cafinc.mccsys.ca/portal-chooser';
const PS_PORTAL_URL = 'https://cafps.mccsys.ca/portal-chooser';
const TRADE_ACCOUNT_URL = 'https://mccsys.ca/store/cafinc/trade-account';

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const { theme } = useTheme();
  
  const isFencePage = location.pathname.toLowerCase().startsWith('/fence') || location.pathname === '/';
  const isPsPage = location.pathname.toLowerCase().startsWith('/ps');
  
  // Colors based on division
  const primaryColor = isPsPage ? '#456CAC' : '#456CAC';
  const primaryColorDark = isPsPage ? '#3A5A91' : '#3A5A91';
  
  // Portal URL based on division
  const portalUrl = isPsPage ? PS_PORTAL_URL : FENCE_PORTAL_URL;
  
  const mainContactInfo = {
    phone: "(403) 986-5484",
    email: "orders@cafinc.ca"
  };
  
  const getContactInfo = () => {
    if (isPsPage) return companyInfo.propertyServices;
    return mainContactInfo;
  };
  
  const contactInfo = getContactInfo();
  
  const getLogo = () => {
    if (location.pathname.startsWith('/fence')) return fenceLogoColor;
    if (isPsPage) return psLogoColor;
    return landingLogoColor;
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const colors = {
    primary: primaryColor,
    primaryDark: primaryColorDark,
    bg: theme === 'light' 
      ? scrolled ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.95)'
      : scrolled ? 'rgba(10,10,10,0.98)' : 'rgba(10,10,10,0.95)',
    text: theme === 'light' ? '#374151' : '#e5e7eb',
    textHover: primaryColor,
    border: theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)',
    surface: theme === 'light' ? '#ffffff' : '#18181b',
  };

  // Navigation structure for Fence pages (5 areas)
  const fenceNavigation = {
    products: {
      label: 'Products',
      items: [
        { name: 'Vinyl Fencing', href: '/fence/vinyl', icon: VinylFenceIcon, description: 'Low maintenance, durable' },
        { name: 'Wood Fencing', href: '/fence/wood', icon: WoodFenceIcon, description: 'Classic, natural beauty' },
        { name: 'Chain Link', href: '/fence/chain-link', icon: ChainLinkIcon, description: 'Affordable, secure' },
        { name: 'Ornamental', href: '/fence/ornamental', icon: OrnamentalFenceIcon, description: 'Elegant iron & aluminum' },
        { name: 'Material Calculator', href: '/fence/calculator', icon: Calculator, description: 'Estimate your project' },
        { name: 'Shop All Products', href: STORE_URL, icon: ShoppingCart, external: true, highlight: true },
      ]
    },
    contractors: {
      label: 'Contractors',
      items: [
        { name: 'Contractor Hub', href: '/fence/contractors', icon: Users, description: 'Join our network' },
        { name: 'Trade Accounts', href: '/fence/trade-account', icon: Briefcase, description: 'Pricing & rewards' },
        { name: 'Spec Sheets', href: '/fence/specs', icon: FileText, description: 'Technical documents' },
      ]
    },
    resources: {
      label: 'Resources',
      items: [
        { name: 'Municipal Bylaws', href: '/fence/bylaws', icon: BookOpen, description: 'Local regulations' },
        { name: 'New Developments', href: '/fence/developments', icon: Building2, description: 'Subdivision guidelines' },
        { name: 'Project Gallery', href: '/fence/gallery', icon: Image, description: 'See our work' },
        { name: 'Service Area', href: '/fence/service-area', icon: MapPin, description: 'Where we deliver' },
      ]
    },
    help: {
      label: 'Help',
      items: [
        { name: 'FAQ', href: '/fence/faq', icon: HelpCircle, description: 'Common questions' },
        { name: 'Warranty Info', href: '/fence/warranty', icon: Shield, description: 'Coverage details' },
        { name: 'Payment Options', href: '/fence/payments', icon: CreditCard, description: 'How to pay' },
        { name: 'Financing', href: '/fence/financing', icon: Percent, description: 'Monthly payment plans' },
      ]
    },
    about: {
      label: 'About',
      items: [
        { name: 'About Us', href: '/fence/about', icon: Users, description: 'Our story' },
        { name: 'Blog & Guides', href: '/fence/blog', icon: FileText, description: 'Tips & advice' },
        { name: 'Contact Us', href: '/fence/contact', icon: Phone, description: 'Get in touch' },
      ]
    }
  };

  // Navigation structure for Property Services pages (5 areas)
  const psNavigation = {
    services: {
      label: 'Services',
      items: [
        { name: 'Snow Removal', href: '/ps/snow', icon: Snowflake, description: '24/7 emergency service', highlight: true },
        { name: 'Landscape Maintenance', href: '/ps/landscape', icon: Trees, description: 'Complete lawn care' },
        { name: 'Parking Lot Repair', href: '/ps/asphalt', icon: LayoutGrid, description: 'Asphalt & concrete' },
        { name: 'Line Painting', href: '/ps/line-painting', icon: Paintbrush, description: 'Parking lot striping' },
        { name: 'Curb Repair', href: '/ps/curb-repair', icon: Wrench, description: 'Curb restoration' },
        { name: 'Crack Sealing', href: '/ps/crack-sealing', icon: Shield, description: 'Preventive maintenance' },
        { name: 'Signage', href: '/ps/signage', icon: AlertOctagon, description: 'Stop signs & parking signs' },
      ]
    },
    resources: {
      label: 'Resources',
      items: [
        { name: 'Project Gallery', href: '/ps/gallery', icon: Image, description: 'See our work' },
        { name: 'Blog', href: '/ps/blog', icon: BookOpen, description: 'Tips & guides' },
        { name: 'Service Area', href: '/ps/service-area', icon: MapPin, description: 'Coverage map' },
      ]
    },
    billing: {
      label: 'Billing',
      items: [
        { name: 'Payment Options', href: '/ps/payments', icon: CreditCard, description: 'Billing & terms' },
        { name: 'Customer Portal', href: 'https://mccsys.ca', icon: ExternalLink, external: true, description: 'Track services' },
      ]
    },
    support: {
      label: 'Support',
      items: [
        { name: 'FAQ', href: '/ps/faq', icon: HelpCircle, description: 'Common questions' },
        { name: 'Terms & Conditions', href: '/ps/terms', icon: FileCheck, description: 'Service terms' },
      ]
    },
    about: {
      label: 'About',
      items: [
        { name: 'About Us', href: '/ps/about', icon: Users, description: 'Our team' },
        { name: 'Contact Us', href: '/ps/contact', icon: Phone, description: 'Get in touch' },
      ]
    }
  };

  const navigation = isPsPage ? psNavigation : fenceNavigation;

  const toggleDropdown = (key) => {
    setActiveDropdown(activeDropdown === key ? null : key);
  };

  const NavDropdown = ({ navKey, nav }) => {
    const isOpen = activeDropdown === navKey;
    const hasItems = nav.items && nav.items.length > 0;
    
    if (!hasItems && nav.href) {
      return (
        <Link
          to={nav.href}
          className="text-sm font-medium transition-all duration-200 py-2 px-1 hover:opacity-80"
          style={{ color: colors.text }}
        >
          {nav.label}
        </Link>
      );
    }

    return (
      <div className="relative">
        <button
          onClick={() => toggleDropdown(navKey)}
          className="text-sm font-medium transition-all duration-200 flex items-center gap-1 py-2 px-1"
          style={{ color: isOpen ? colors.primary : colors.text }}
          data-testid={`${navKey}-nav-dropdown`}
        >
          {nav.label}
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div 
            className="absolute top-full left-0 mt-2 w-72 rounded-xl shadow-2xl py-2 z-50 border animate-in fade-in slide-in-from-top-2 duration-200"
            style={{ backgroundColor: colors.surface, borderColor: colors.border }}
          >
            {nav.items.map((item, index) => (
              item.external ? (
                <a 
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-start gap-3 px-4 py-3 transition-all duration-150 hover:bg-opacity-10 ${item.highlight ? 'bg-opacity-5' : ''}`}
                  style={{ 
                    backgroundColor: item.highlight ? `${colors.primary}10` : 'transparent',
                    color: item.highlight ? colors.primary : colors.text 
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${colors.primary}15`}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = item.highlight ? `${colors.primary}10` : 'transparent'}
                >
                  {item.icon && <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: colors.primary }} />}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${item.highlight ? 'font-semibold' : ''}`}>{item.name}</span>
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    </div>
                    {item.description && (
                      <p className="text-xs mt-0.5 opacity-60">{item.description}</p>
                    )}
                  </div>
                </a>
              ) : (
                <Link 
                  key={item.name}
                  to={item.href}
                  className="flex items-start gap-3 px-4 py-3 transition-all duration-150"
                  style={{ color: colors.text }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${colors.primary}10`}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {item.icon && <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: colors.primary }} />}
                  <div className="flex-1">
                    <span className="text-sm font-medium">{item.name}</span>
                    {item.description && (
                      <p className="text-xs mt-0.5 opacity-60">{item.description}</p>
                    )}
                  </div>
                </Link>
              )
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <header 
      className={`fixed left-0 right-0 z-50 transition-all duration-300 w-full top-0 ${scrolled ? 'shadow-lg' : ''}`}
      style={{ 
        backgroundColor: colors.bg, 
        borderBottom: `1px solid ${colors.border}`,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)'
      }}
    >
      {/* Top utility bar */}
      <div 
        className="w-full border-b"
        style={{ 
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          borderColor: 'rgba(0,0,0,0.1)' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-white/90 hidden md:block font-medium">
              {isPsPage ? "Central Alberta's Property Maintenance Experts" : "Central Alberta's Fence Supply Experts"}
            </span>
            <div className="flex items-center gap-4 sm:gap-6 mx-auto md:mx-0">
              <a 
                href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-1.5 text-white/90 hover:text-white transition-colors"
              >
                <Phone className="w-3.5 h-3.5" />
                <span className="font-medium">{contactInfo.phone}</span>
              </a>
              <a 
                href={`mailto:${contactInfo.email}`}
                className="hidden sm:flex items-center gap-1.5 text-white/90 hover:text-white transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                <span className="font-medium">{contactInfo.email}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo - Now larger */}
          <Link to={isPsPage ? '/ps' : '/fence'} className="flex-shrink-0">
            <img 
              src={getLogo()} 
              alt={isPsPage ? "CAF Property Services" : "Central Alberta Fencing"} 
              className="h-10 sm:h-12 lg:h-14 w-auto object-contain"
            />
          </Link>
          
          {/* Desktop Navigation - Clean & Minimal */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2" ref={dropdownRef}>
            {/* Services shown directly on XL screens */}
            {isPsPage ? (
              <>
                {/* PS Services - Direct links on wide screens with icons */}
                <div className="hidden 2xl:flex items-center gap-1">
                  {psNavigation.services.items.slice(0, 4).map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="text-sm font-medium transition-all duration-200 py-2 px-2 xl:px-3 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 whitespace-nowrap flex items-center gap-1.5"
                      style={{ color: location.pathname === item.href ? colors.primary : colors.text }}
                    >
                      {item.icon && <item.icon className="w-4 h-4" style={{ color: colors.primary }} />}
                      {item.name}
                    </Link>
                  ))}
                </div>
                {/* Services dropdown on smaller screens */}
                <div className="2xl:hidden">
                  <NavDropdown navKey="services" nav={psNavigation.services} />
                </div>
                {/* All 5 nav dropdowns for PS */}
                <NavDropdown navKey="resources" nav={psNavigation.resources} />
                <NavDropdown navKey="billing" nav={psNavigation.billing} />
                <NavDropdown navKey="support" nav={psNavigation.support} />
                <NavDropdown navKey="about" nav={psNavigation.about} />
              </>
            ) : (
              <>
                {/* Fence Products - Direct links on wide screens with icons */}
                <div className="hidden 2xl:flex items-center gap-1">
                  {fenceNavigation.products.items.filter(item => !item.external).slice(0, 4).map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="text-sm font-medium transition-all duration-200 py-2 px-2 xl:px-3 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 whitespace-nowrap flex items-center gap-1.5"
                      style={{ color: location.pathname === item.href ? colors.primary : colors.text }}
                    >
                      {item.icon && <item.icon className="w-4 h-4" style={{ color: colors.primary }} />}
                      {item.name}
                    </Link>
                  ))}
                </div>
                {/* Products dropdown on smaller screens */}
                <div className="2xl:hidden">
                  <NavDropdown navKey="products" nav={fenceNavigation.products} />
                </div>
                {/* All 5 nav dropdowns */}
                <NavDropdown navKey="contractors" nav={fenceNavigation.contractors} />
                <NavDropdown navKey="resources" nav={fenceNavigation.resources} />
                <NavDropdown navKey="help" nav={fenceNavigation.help} />
                <NavDropdown navKey="about" nav={fenceNavigation.about} />
              </>
            )}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Shop Now - Only show on Fence pages */}
            {!isPsPage && (
              <a href={STORE_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="rounded-lg px-5 py-2.5 font-semibold shadow-md hover:shadow-lg transition-all text-white"
                  style={{ backgroundColor: colors.primary }}
                  data-testid="header-shop-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Shop Now
                </Button>
              </a>
            )}
            
            {/* Get Quote button for PS pages */}
            {isPsPage && (
              <Link to="/ps/snow">
                <Button 
                  className="rounded-lg px-5 py-2.5 font-semibold shadow-md hover:shadow-lg transition-all text-white"
                  style={{ backgroundColor: colors.primary }}
                  data-testid="header-get-quote-btn"
                >
                  Get a Quote
                </Button>
              </Link>
            )}
            
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" title="Portal Login">
              <Button 
                variant="outline" 
                className="rounded-lg px-3 py-2.5 transition-all"
                style={{ borderColor: colors.border, color: colors.text }}
              >
                <LogIn className="w-4 h-4" />
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg transition-colors"
            style={{ color: colors.text }}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden border-t animate-in slide-in-from-top duration-200"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {Object.entries(navigation).map(([key, nav]) => (
              <div key={key} className="border-b pb-2 mb-2" style={{ borderColor: colors.border }}>
                {nav.items ? (
                  <>
                    <button
                      onClick={() => toggleDropdown(key)}
                      className="w-full flex items-center justify-between py-3 text-base font-medium"
                      style={{ color: colors.text }}
                    >
                      {nav.label}
                      <ChevronDown className={`w-5 h-5 transition-transform ${activeDropdown === key ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === key && (
                      <div className="pl-4 space-y-1 pb-2">
                        {nav.items.map((item) => (
                          item.external ? (
                            <a
                              key={item.name}
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 py-2.5 text-sm"
                              style={{ color: item.highlight ? colors.primary : colors.text }}
                            >
                              {item.icon && <item.icon className="w-4 h-4" style={{ color: colors.primary }} />}
                              {item.name}
                              <ExternalLink className="w-3 h-3 opacity-50" />
                            </a>
                          ) : (
                            <Link
                              key={item.name}
                              to={item.href}
                              className="flex items-center gap-3 py-2.5 text-sm"
                              style={{ color: colors.text }}
                            >
                              {item.icon && <item.icon className="w-4 h-4" style={{ color: colors.primary }} />}
                              {item.name}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={nav.href}
                    className="block py-3 text-base font-medium"
                    style={{ color: colors.text }}
                  >
                    {nav.label}
                  </Link>
                )}
              </div>
            ))}
            
            {/* About link */}
            <Link
              to={isPsPage ? '/ps/about' : '/fence/about'}
              className="block py-3 text-base font-medium"
              style={{ color: colors.text }}
            >
              About
            </Link>

            {/* Mobile CTA buttons */}
            <div className="pt-4 space-y-3">
              <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="block">
                <Button 
                  className="w-full rounded-lg py-3 font-semibold text-white"
                  style={{ backgroundColor: colors.primary }}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Shop Now
                </Button>
              </a>
              <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="block">
                <Button 
                  variant="outline" 
                  className="w-full rounded-lg py-3"
                  style={{ borderColor: colors.border, color: colors.text }}
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Portal Login
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
